import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';

import { ClosableModalWithErrorResponseModel } from '../../../shared/components/base/closable-modal-with-error-response-model';
import {
  NotificationService,
  CompSearchService,
  SessionService,
  UtilsService,
  EntitiesService,
  ReportRepositoryService
} from '../../../shared/services';
import { ModalService } from '../../../shared/modules/modal';
import {
  Container,
  FinancialDataValueEnteredInEnum,
  BaseOption,
  MasterFileGenerateReportModalPayload,
  LanguageIsoInfo
} from '../../../shared/models';
import { AllowedLimitWordingRoles } from '../../../shared/models/user-permissions-policy';

import { TinyTextEditor } from '../../../shared/constants';
import { MasterFileGenerateReportPayload } from '../../../shared/models/master-file-generate-report-payload';
import { CountriesService } from '../../../shared/services/countries.service';
import { Currency } from '../../../shared/models/currency';
import { TpReportGeneratorService } from '../../../shared/services/tp-report-generator.service';
import { ReportTemplates } from '@xbs/xbs-enums';
import { Country } from '../../../shared/models/country';
import { ListResponse, ObjectResponse } from '../../../shared/interfaces/response';
import { BaseCountry } from '../../../shared/models/base-country';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'report-generation-modal',
  templateUrl: './report-generation-modal.component.html',
  styleUrls: ['./report-generation-modal.component.scss']
})
export class ReportGenerationModalComponent
  extends ClosableModalWithErrorResponseModel<any>
  implements OnInit {

  data: MasterFileGenerateReportModalPayload;
  taxJurisdictions: BaseCountry[];
  taxJurisdictionCurrency: Currency;
  languages: LanguageIsoInfo[];
  currencyOptions: BaseOption[];
  generateMasterFileReportForm: FormGroup;
  latestReportLanguageIso: string;

  private workingContainer: Container;

  shouldDisplayPageNumber: boolean = true;
  tinyInit = TinyTextEditor.INIT;
  shouldLimitWording: boolean;
  userRole: string;
  isAllowedLimitWording: boolean = false;

  constructor(
    protected notificationService: NotificationService,
    protected modalService: ModalService,
    private _formBuilder: FormBuilder,
    private _sessionService: SessionService,
    private _compSearchService: CompSearchService,
    private _utilsService: UtilsService,
    private _entitiesService: EntitiesService,
    private _countriesService: CountriesService,
    private _tpReportGeneratorService: TpReportGeneratorService,
    private _reportService: ReportRepositoryService,
    private _translateService: TranslateService
  ) {
    super(notificationService, modalService);
  }

  ngOnInit() {
    this.userRole = this._sessionService.getUserRole();
    this.isAllowedLimitWording = AllowedLimitWordingRoles.includes(this.userRole);
    this.shouldLimitWording = this.isAllowedLimitWording;
    this.workingContainer = this._sessionService.getWorkingContainer();
    this.generateMasterFileReportForm = this._formBuilder.group({
      reportTitle: ['', Validators.required],
      taxJurisdiction: ['', Validators.required],
      language: ['', Validators.required],
      currencyType: ['', Validators.required],
      executiveSummary: ['', Validators.required],
    });

    this.getFormData();
  }

  getReportTitleFormControl(): AbstractControl { return this.generateMasterFileReportForm.get('reportTitle'); }
  getTaxJurisdictionFormControl(): AbstractControl { return this.generateMasterFileReportForm.get('taxJurisdiction'); }
  getLanguageFormControl(): AbstractControl { return this.generateMasterFileReportForm.get('language'); }
  getCurrencyTypeFormControl(): AbstractControl { return this.generateMasterFileReportForm.get('currencyType'); }
  getExecutiveSummaryFormControl(): AbstractControl { return this.generateMasterFileReportForm.get('executiveSummary'); }

  generateReport(): void {
    Object.keys(this.generateMasterFileReportForm.controls).forEach((controlKey) => {
      this.generateMasterFileReportForm.controls[controlKey].markAsTouched();
    });

    if (this.generateMasterFileReportForm.valid) {
      this.isSaveDisabled = true;
      this.generateInternalMasterFileReport();
    }
  }

  close(): void {
    this.closeAllModals();
  }

  private getFormData(): void {
    this._entitiesService.list(`?sort=name&order=asc`).subscribe((entities) => {
      this.taxJurisdictions = entities.data.map(entity => entity.taxJurisdiction);
      this.taxJurisdictions = Array.from(this.taxJurisdictions.reduce((uniqueJurisdictions, jurisdiction) => uniqueJurisdictions.set(jurisdiction.countryId, jurisdiction), new Map()).values());
      this.taxJurisdictions.sort((jurisdiction1, jurisdiction2) => {
        return jurisdiction1.name > jurisdiction2.name ? 1 : -1;
      });

      if (this.data.report) {
        this.subscriptions.push(this._reportService.getReportInstance(this.data.report.reportId, this.data.reportInstanceId).subscribe((reportInstance) => {
          this.data.latestGeneratedVersion = reportInstance;
          this.latestReportLanguageIso = this.data.latestGeneratedVersion.languageIso;
          this.shouldDisplayPageNumber = this.data.latestGeneratedVersion.shouldDisplayPageNumber;

          if (this.data.isNewVersion) {
            const currency = this.data.mneCurrency.currencyId === this.data.latestGeneratedVersion.reportCurrency.currencyId ? 1 : 2;
            const jurisdiction = this.taxJurisdictions.find(jur => jur.isoCode === this.data.latestGeneratedVersion.taxJurisdictionCode);
            this.getReportTitleFormControl().setValue(this.data.report.name);
            this.getReportTitleFormControl().disable();
            this.getTaxJurisdictionFormControl().setValue(jurisdiction);
            this.getLanguageFormControl().setValue(this.data.latestGeneratedVersion.languageIso);
            this.getCurrencyTypeFormControl().setValue(currency);
            this.getExecutiveSummaryFormControl().setValue(this.data.latestGeneratedVersion.executiveSummaryIntro);

            this.getLanguages();
            this.getTaxJurisdictionCurrency();
            this.getCurrencyOptions();
          }
        }));
      }
    });
  }

  taxJurisdictionChange(): void {
    this.getLanguages();
    this.getTaxJurisdictionCurrency();
    this.getCurrencyOptions();
  }

  private getLanguages(): void {
    const taxYear = this.workingContainer.taxYear;

    if (this.getTaxJurisdictionFormControl().value &&
        this.getTaxJurisdictionFormControl().value.countryId) {

      const jurisdictionId = this.getTaxJurisdictionFormControl().value.countryId;

      this._compSearchService.getLanguages(jurisdictionId, taxYear).subscribe(({ data: languages }: ObjectResponse<LanguageIsoInfo[]>) => {
        this.languages = languages;
        this.languages.forEach((language) => {
          /* eslint camelcase: ['error', { allow: ['language_iso'] }] */
          language.displayName = this._translateService.instant(`internal_local_file_report.languages.${language.language_iso}`);
        });

        this.languages.sort((language1, language2) => {
          return language1.displayName > language2.displayName ? 1 : -1;
        });

        this.setLanguageSelection(languages);
      });
    }
  }

  private setLanguageSelection(languages: LanguageIsoInfo[]): void {
    if (languages && languages.length > 0) {
      const languageToUseInfo: LanguageIsoInfo = this._utilsService.findDefaultLanguage(languages, this.latestReportLanguageIso);

      this.getLanguageFormControl().setValue(languageToUseInfo.language_iso);
    }
  }

  private getCurrencyOptions(): void {
    this.currencyOptions = this._utilsService.enumToArray(FinancialDataValueEnteredInEnum);
  }

  private getTaxJurisdictionCurrency(): void {
    this._countriesService.list().subscribe((countries: ListResponse<Country>) => {
      const taxJurisdictionCountry = countries.data.find(
        country => country.countryId === this.getTaxJurisdictionFormControl().value.countryId
      );
      if (taxJurisdictionCountry) {
        this.taxJurisdictionCurrency = taxJurisdictionCountry.defaultCurrency;
      }
    });
  }

  getCurrencyIsoCode(optionId: number): string {
    let currency: string = this.getTaxJurisdictionFormControl().value && this.taxJurisdictionCurrency && this.taxJurisdictionCurrency.isoCode;
    if (optionId === FinancialDataValueEnteredInEnum.MNE) {
      currency = this.data && this.data.mneCurrency && this.data.mneCurrency.isoCode;
    }

    return currency;
  }

  toggleDisplayPageNumbers(): void {
    this.shouldDisplayPageNumber = !this.shouldDisplayPageNumber;
  }

  toggleLimitationWording(): void {
    this.shouldLimitWording = !this.shouldLimitWording;
  }

  private generateInternalMasterFileReport(): void {
    const payload = this.createPayload();

    this._tpReportGeneratorService.generateMasterFileReport(payload).subscribe(() => {
      this.success();
      this.exitModal();
    });
  }

  exitModal(): void {
    this.closeAllModals();
  }

  private createPayload(): MasterFileGenerateReportPayload {
    return {
      masterFileReportId: this.data.isNewVersion && this.data.report ? this.data.report.reportId : null,
      templateName: ReportTemplates.ByName.MasterFileTemplate.Value,
      mneId: this._sessionService.getContainerUpeId(),
      name: this.getReportTitleFormControl().value,
      currencyId: this.getReportGenerationPayloadCurrencyId(),
      executiveSummaryIntroduction: this.getExecutiveSummaryFormControl().value,
      taxJurisdictionCode: this.getTaxJurisdictionFormControl().value.isoCode,
      taxYear: this.workingContainer.taxYear,
      targetLanguage: this.getLanguageFormControl().value,
      containerId: this.workingContainer.containerId,
      newReportVersion: !!this.data.isNewVersion,
      shouldDisplayPageNumber: this.shouldDisplayPageNumber,
      isLimitationWording: this.shouldLimitWording
    };
  }

  private getReportGenerationPayloadCurrencyId(): number {
    let currencyId: number;
    const currencyTypeId = Number(this.getCurrencyTypeFormControl().value);

    if (currencyTypeId === 1) {
      currencyId = this.data.mneCurrency.currencyId;
    } else if (currencyTypeId === 2) {
      currencyId = this.taxJurisdictionCurrency.currencyId;
    }

    return currencyId;
  }

}
